'use client';

import { type FC, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useFormState } from 'react-use-form-state';
import { z } from 'zod';

import Button from 'molecules/Button';
import ContactSidebar from 'molecules/ContactSideBar';
import SelectInput from 'molecules/Form/selectinput/selectinput';
import Textarea from 'molecules/Form/textarea/textarea';
import TextInput from 'molecules/Form/textinput/textinput';

import { usStates } from 'components/Forms/AgentQuote/data/states';
import styles from 'components/Forms/TechSupport/techsupport.module.scss';

import { formZodSchema } from 'utils/formZodSchema';
import { debounce, formatPhoneNumber, isNonEmptyArray } from 'utils/functions';
import { ENV_KEY_MAPS, getEnvValue } from 'utils/getEnvValue';
import SendGtmEvent from 'utils/hooks/sendGtmEvent';

import type { TechSupportProps } from 'components/Forms/TechSupport/config';
import type { FormEvent } from 'react';
import type { ReCAPTCHAProps } from 'react-google-recaptcha';

const ReCAPTCHAComponent = ReCAPTCHA as unknown as React.ForwardRefExoticComponent<
  React.PropsWithoutRef<ReCAPTCHAProps> & React.RefAttributes<ReCAPTCHA>
>;

const TechSupport: FC<TechSupportProps> = ({ addPadding, formFields, url, redirect_url, form_url, contactSideBar }) => {
  const [formState, { text, email, tel, textarea, select }] = useFormState();
  const [formError, setFormError] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const { validity, values } = formState;
  const touched = useRef<Record<string, boolean>>({});

  const reCaptcha = true;
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const formUrl = getEnvValue(form_url, ENV_KEY_MAPS.urls);
  console.log(formUrl);

  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';

  // Stores debounced validators per fields
  const changeDebounceMap = useRef<Record<string, ReturnType<typeof debounce>>>({});
  const blurDebounceMap = useRef<Record<string, ReturnType<typeof debounce>>>({});

  const [inputErrorMessages, setInputErrorMessages] = useState<Record<string, string>>({});
  // Build required field forms
  const requiredFields = isNonEmptyArray(formFields)
    ? formFields
        .filter(field => field.required)
        .map(field => field.fieldName)
        .filter((fieldName): fieldName is string => Boolean(fieldName))
    : [];

  // Schema Key fo zodSchema
  const nameToSchemaKey: Record<string, keyof typeof formState.values> = {
    'contact-name': 'contactName',
    company: 'company',
    email: 'email',
    phone: 'phone',
  };

  const validateFieldInput = (fieldName: string, value: string, required?: boolean) => {
    const requiredField = required;
    const validationValue = value;
    const formFieldName = nameToSchemaKey[fieldName];

    setInputErrorMessages(prev => {
      const nextErrorMessages = { ...prev };
      // Validation for radio and select inputs
      if (fieldName === 'description' && values['reason'] === 'Other (please provide information in description)') {
        if (!validationValue) {
          formState.setFieldError(fieldName, false);
          nextErrorMessages[fieldName] = 'This field is required.';
        } else {
          formState.setField(fieldName, validationValue);
          formState.validity[fieldName] = true;
          nextErrorMessages[fieldName] = '';
        }
      } else if (requiredField === true && validationValue?.trim().length === 0) {
        formState.setFieldError(fieldName, false);
        nextErrorMessages[fieldName] = 'This field is required.';
      } else {
        try {
          const partialSchema = formZodSchema.pick({ [formFieldName]: true } as any);
          partialSchema.parse({ [formFieldName]: validationValue });
          nextErrorMessages[fieldName] = '';
          formState.validity[fieldName] = true;
        } catch (error) {
          if (error instanceof z.ZodError) {
            const message = error.flatten().fieldErrors[formFieldName]?.[0] || 'Invalid input';
            nextErrorMessages[fieldName] = message;
            formState.setFieldError(fieldName, true);
          }
        }
      }

      const hasError = Object.values(nextErrorMessages).some(error => error.length > 0);

      // Check for unfilled required fields
      const { values: currentValues } = formState;
      const requiredFieldsEntered = requiredFields.every(field => {
        const val = currentValues[field];

        return typeof val === 'string' && val.trim().length > 0;
      });

      if (hasError) {
        setFormError('Please correct the highlighted fields.');
        setFormStatus('error');
      } else if (!requiredFieldsEntered) {
        setFormError('Please complete all required fields.');
        setFormStatus('error');
      } else {
        setFormError('');
        setFormStatus('success');
      }

      return nextErrorMessages;
    });
  };

  // validates on change with debounce to give user time to type
  const validateOnChange = (fieldName: string, value: string, required?: boolean) => {
    // Check for empty input
    touched.current[fieldName] = true;
    if (!changeDebounceMap.current[fieldName]) {
      changeDebounceMap.current[fieldName] = debounce((latestValue: string) => {
        // validation step
        if (touched.current[fieldName]) {
          validateFieldInput(fieldName, latestValue, required);
        }
      }, 200);
    }

    changeDebounceMap.current[fieldName](value);
  };

  const validateOnBlur = (fieldName: string, value: string, required?: boolean) => {
    if (!blurDebounceMap.current[fieldName]) {
      touched.current[fieldName] = true;

      blurDebounceMap.current[fieldName] = debounce((latestValue: string) => {
        if (fieldName === 'phone') {
          const digits = latestValue.replace(/\D/g, '');
          const formatted = formatPhoneNumber(digits);

          formState.setField(fieldName, formatted); // Display formatted
          validateFieldInput(fieldName, digits); // Validate only raw digits

          return;
        }

        // Normal case for non-phone inputs
        if (latestValue && (!latestValue || latestValue.trim() === '')) {
          formState.setFieldError(fieldName, false);
        }

        validateFieldInput(fieldName, latestValue, required);
      }, 300);
    }

    blurDebounceMap.current[fieldName](value);
  };

  // Handle Form Submission
  const handleSubmit = (e: FormEvent) => {
    const honeypotValue = (e.target as HTMLFormElement).username?.value;
    if (honeypotValue && honeypotValue.trim() !== '') {
      e.preventDefault();
      setFormStatus('error');

      return;
    }

    let hasErrors = false;

    requiredFields.forEach(field => {
      const value = values[field];
      formState.setField(field, value);
      Object.keys(inputErrorMessages).forEach(key => {
        if (inputErrorMessages[key] && key === field) {
          validateFieldInput(field, value);
          hasErrors = true;
        }
      });

      // Validate description field on submit if 'Other' was selected from dropdown
      if (values['reason'] === 'Other (please provide information in description)' && values['description'] === '') {
        hasErrors = true;
        validateFieldInput('description', values['description']);
        setFormError('Please fill out all required fields.');
      }

      if (!value) {
        hasErrors = true;
        setFormError('Please fill out all required fields.');
        formState.setFieldError(field, true);
      }
    });
    if (hasErrors) {
      e.preventDefault();
      setFormStatus('error');

      return;
    }

    if (reCaptcha && !captchaToken) {
      e.preventDefault();
      setFormError('Please complete the reCAPTCHA.');
      setFormStatus('error');

      return;
    }

    setFormStatus('success');
    setFormError('');
  };

  const getFormStateProps = (type: 'text' | 'email' | 'tel', fieldName: string) => {
    const handlers: Record<'text' | 'email' | 'tel', typeof text> = {
      text,
      email,
      tel,
    };

    return {
      ...handlers[type]({
        name: fieldName,
        validateOnBlur: true,
      }),
    };
  };

  const handleCaptchaChange = async (token: string | null) => {
    try {
      if (!token) {
        setFormError('Please complete the reCAPTCHA.');

        return;
      }

      const res = await fetch('/api/recaptcha', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setCaptchaToken(token);
      } else {
        setFormError('reCAPTCHA verification failed.');
      }
    } catch (error) {
      setFormError('Error verifying reCAPTCHA. Please try again.');
    }
  };

  return (
    <div className={`${styles.containerWrapper} ${addPadding ? styles.addPadding : styles.removePadding}`}>
      {formStatus !== 'idle' && (
        <SendGtmEvent
          variant="contact-one-model-form"
          eventData={{
            formSpecialty: values['Primary Specialty'],
            formState: values['State'],
            formZip: values['Zip'],
            formSubmitSuccessful: formStatus === 'success' ? true : false,
          }}
        />
      )}
      <div className={styles.containerText}>
        <div className="container">
          <form onSubmit={handleSubmit} action={url} method="POST" className={styles.formContainer}>
            <fieldset className={styles.fieldset}>
              <input type="hidden" name="orgid" value="00DA0000000gcHm" />
              <input type="hidden" name="retURL" value={redirect_url} />
              <input type="hidden" name="type" value="General" />
              <input
                type="text"
                name="username"
                id="username"
                className={styles.username}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              {formError && <p className={styles.errorText}>{formError}</p>}
              {isNonEmptyArray(formFields) &&
                formFields.map(({ type = 'text', fieldName = '', label = '', required, options, helpText }, index) => (
                  <div key={index}>
                    {(type === 'text' || type === 'tel' || type === 'email') && (
                      <TextInput
                        id={fieldName}
                        label={label}
                        maxLength={80}
                        required={required}
                        aria-labelledby={fieldName}
                        helpText={helpText}
                        message={inputErrorMessages[fieldName]}
                        validateOnBlur={validateOnBlur}
                        validateOnChange={validateOnChange}
                        formStateProps={getFormStateProps(type, fieldName)}
                        valid={validity[fieldName]}
                      />
                    )}
                    {(type === 'select' || type === 'state') && (
                      <>
                        <SelectInput
                          id={fieldName}
                          label={label}
                          options={
                            type === 'state'
                              ? usStates.map(({ abbreviation }) => ({ name: abbreviation, abbreviation }))
                              : options || []
                          }
                          required
                          formStateProps={{
                            ...select({
                              name: fieldName,
                              validateOnBlur: true,
                            }),
                          }}
                          valid={validity[fieldName]}
                          validateOnBlur={validateOnBlur}
                          validateOnChange={validateOnChange}
                        />
                        {type === 'select' &&
                          fieldName === 'reason' &&
                          values[fieldName] === 'Other (please provide information in description)' && (
                            <Textarea
                              id="description"
                              label="Description"
                              required={true}
                              formStateProps={{
                                ...textarea({
                                  name: 'description',
                                  validateOnBlur: true,
                                }),
                              }}
                              validateOnBlur={validateOnBlur}
                              validateOnChange={validateOnChange}
                              valid={validity['description']}
                            />
                          )}
                      </>
                    )}
                    {type === 'textarea' && (
                      <Textarea
                        id={fieldName}
                        label={label}
                        required
                        formStateProps={{
                          ...textarea({
                            name: fieldName,
                            validateOnBlur: true,
                          }),
                        }}
                        valid={validity[fieldName]}
                      />
                    )}
                  </div>
                ))}
              {reCaptcha && (
                <ReCAPTCHAComponent ref={recaptchaRef} sitekey={siteKey as string} onChange={handleCaptchaChange} />
              )}
              <Button color="teal" id="contact-form-submit-button" type="submit" name="submit">
                Submit
              </Button>
            </fieldset>
          </form>
          <br />
        </div>
      </div>
      <div className={styles.sideNav}>
        <ContactSidebar {...contactSideBar} />
      </div>
    </div>
  );
};

export default TechSupport;
